/**
 * @fileoverview addTransactionBuilder - Build new transaction objects from form data.
 *
 * Converts form state into properly formatted transaction objects for API submission.
 * Handles the complex logic of different transaction types and category amount signs.
 *
 * @description Transaction type differences:
 * - Expense: Negative amounts, categories get negative amounts, user category added
 * - Income: Positive amounts, categories get positive amounts
 * - Transfer: Positive amounts, regular categories negative, user categories negative
 * - Adjustment: Two categories (from negative, to positive)
 *
 * Category amount rules:
 * - Income: All positive (credits to budget)
 * - Expense: All negative (debits from budget), plus positive user category if not paid directly
 * - Transfer: Source categories negative, user categories negative
 * - Adjustment: From category negative, to category positive
 *
 * @module hooks/helpers/addTransactionBuilder
 */
import { getBudgetMonth } from '../../utils/dateUtils';

/**
 * Builds a new transaction object from form data.
 *
 * @param {Object} formData - Form state from useTransactionForm
 * @param {Array<string>} userCategories - User category names
 * @param {Array<Object>} users - Array of user objects with userKey and userName
 * @returns {Object} Transaction object ready for API submission
 *
 * @example
 * const newTransaction = buildNewTransaction(formState, userCategories, users);
 * // Returns: { id, amount, description, categories, date, type, budgetMonth, ... }
 */
export const buildNewTransaction = (formData, userCategories, users = []) => {
    const {
        transactionType,
        amount,
        description,
        adjustmentFromCategory,
        adjustmentToCategory,
        date,
        currentUserKey,
        transactionCategories,
        paidDirectly,
        useTemplateForIncome
    } = formData;

    // Look up the user's category name from their userKey
    const currentUser = users.find(u => u.userKey === currentUserKey)?.userName || null;

    if (transactionType === 'Adjustment') {
        return {
            id: Date.now(),
            amount: parseFloat(amount),
            description: description.trim(),
            categories: [
                { category: adjustmentFromCategory, amount: -parseFloat(amount) },
                { category: adjustmentToCategory, amount: parseFloat(amount) }
            ],
            adjustmentFromCategory,
            adjustmentToCategory,
            date,
            type: transactionType,
            budgetMonth: getBudgetMonth(date),
            usedTemplate: false,
            paidByUserKey: currentUserKey,
            paidDirectly: false
        };
    } else {
        const transactionAmount = transactionType === 'Income'
            ? parseFloat(amount)
            : -parseFloat(amount);
        const transactionCategories_withUser = [...transactionCategories];

        if (transactionType === 'Expense' && !paidDirectly && currentUser) {
            transactionCategories_withUser.push({
                category: currentUser,
                amount: parseFloat(amount).toFixed(2)
            });
        }

        return {
            id: Date.now(),
            amount: transactionAmount,
            description: description.trim(),
            categories: transactionCategories_withUser.map(cat => {
                const categoryAmount = transactionType === 'Transfer'
                    ? parseFloat(amount)
                    : parseFloat(cat.amount);

                if (userCategories.includes(cat.category)) {
                    if (transactionType === 'Transfer') {
                        return { category: cat.category, amount: -categoryAmount };
                    } else {
                        return { category: cat.category, amount: categoryAmount };
                    }
                }

                return {
                    category: cat.category,
                    amount: transactionType === 'Income' ? categoryAmount : -categoryAmount
                };
            }),
            date,
            type: transactionType,
            budgetMonth: getBudgetMonth(date),
            usedTemplate: transactionType === 'Income' ? useTemplateForIncome : false,
            paidByUserKey: currentUserKey,
            paidDirectly: transactionType === 'Expense' ? paidDirectly : false
        };
    }
};
